import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { MutableRefObject } from 'react'
import type * as Y from 'yjs'
import type { Awareness } from 'y-protocols/awareness'
import { useEditor, EditorContent } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import { TextSelection } from '@tiptap/pm/state'
import StarterKit from '@tiptap/starter-kit'
import { TableKit } from '@tiptap/extension-table'
import { Markdown } from 'tiptap-markdown'
import type { Editor } from '@tiptap/react'
import { CodeBlockMermaid } from './CodeBlockMermaid'
import { ImageBlock } from './ImageNodeView'
import { ResizableTable } from './table-resize'
import { SectionNumbers, setSectionLocks, setSectionOffset, type SectionLock } from './SectionNumbers'
import { FindHighlight } from './find-highlight'
import { HeadingLevelKeys } from './heading-level-keys'
import { ProseHighlight } from './highlight-mark'
import { ProseTextColour } from './text-colour-mark'
import { ProseAnchor, revealAnchor } from './anchor-mark'
import { anchorIdOfHref } from '../../lib/anchors'
import { followAnchor, usePendingAnchor } from '../../lib/anchor-nav'
import { isAllowedLinkUri } from './link-url'
import { uploadAndInsertImage } from '../../lib/image-upload'
import { syncYText } from './yt'
import { appendHistory, makeHistoryEntry } from './history-log'
import { CursorOverlay } from './CursorOverlay'
import { useImproveSelection } from './useImproveSelection'
import { useIdle } from './useIdle'
import { useReleased } from './useReleased'
import { editSectionAt, type EditSection } from './pm-sections'
import { useAgentAvailable } from '../../lib/agent-availability'
import { useLang } from '../../i18n'

/** tiptap-markdown augments editor.storage at runtime; read it through a typed view. */
function getMarkdown(editor: Editor): string {
  return (editor.storage as unknown as { markdown: { getMarkdown(): string } }).markdown.getMarkdown()
}

interface RichEditorProps {
  ytext: Y.Text
  ydoc: Y.Doc
  actor: string
  /** Set while an AI apply is in flight so the human-edit capture ignores those writes. */
  suppressRef: MutableRefObject<boolean>
  /** Presence channel for remote cursors (null until the provider is ready). */
  awareness: Awareness | null
  /** Lift the live editor up so an external toolbar can drive its commands. */
  onEditor?: (editor: Editor | null) => void
  /** The § someone holds right now (unexpired `working` revisions) — shaded and read-only. */
  locks?: SectionLock[]
  /** Where this part's § numbering starts inside its artefact (the count of top-level § in the
   *  parts before it). Numbering is continuous across a deliverable, so part 2 opens at §3.
   *  The backend owns this number — it depends on sibling rooms this client never opens. `null`
   *  means the host hasn't heard back yet (e.g. right after opening a different part) — see
   *  SectionNumbers.ts for why that withholds numbers rather than guessing 0. */
  sectionOffset?: number | null
  /** Identifies THIS connection among lock holders. A lock whose `actor` matches is one we took
   *  ourselves, so it does not restrain us; every other lock does — including one held under our
   *  own identity by the member's console agent, which is a separate worker. Defaults to `actor`. */
  connectionId?: string
  /** A § number button was clicked; the host builds and copies the paste-ready reference. */
  onCopyRef?: (number: string, title: string) => void
  /** An edit was rejected because it fell inside someone else's § — never a silent no-op. */
  onLockRejected?: () => void
  /**
   * Mount the editor read-only — the caller has `viewer` access to this bid.
   *
   * An AFFORDANCE, not the gate. The server already refuses a viewer's writes twice over: the REST
   * surface 403s them and the Hocuspocus handshake gives them a `readOnly` connection whose updates
   * are dropped. What this avoids is silently swallowing their keystrokes, which reads as a broken
   * editor. The host shows one visible banner saying why.
   */
  readOnly?: boolean
  /** Bumped by the lock notice's Release (AB#301): leave the §, i.e. stop publishing `editSection`
   *  until the member's next input in the editor. */
  releaseSignal?: number
  /** The part's room key and the collab server's URL (AB#350): a Ctrl/Cmd+click on a link to an
   *  anchor follows it, here or into another part of the artefact (lib/anchor-nav.ts). Omitted =
   *  such a click does nothing. */
  room?: string | null
  wsUrl?: string
  /** A short notice for the member (the Canvas's toast) — a broken anchor link. */
  onNotice?: (message: string) => void
}

/** Stable empty default, so the publish effect below does not fire on every render. */
const NO_LOCKS: SectionLock[] = []

/**
 * WYSIWYG ("Rich") view — renders the document like Word and edits in place. The shared
 * CRDT stays a Markdown Y.Text (the server derives markdown from it), so this editor
 * parses markdown in and serialises markdown back on every change, applying a minimal
 * diff to the Y.Text. Remote edits (other clients / AI) flow back in when this editor is
 * not the origin. It is the single editing surface for a part.
 *
 * Selecting text reveals an "Improve with AI" action: the local agent rewrites just that
 * excerpt per an optional instruction, the selection is replaced in place, and the change
 * is recorded in the room's modification history as an 'ai' edit (prompt = the instruction).
 */
export function RichEditor({
  ytext,
  ydoc,
  actor,
  suppressRef,
  awareness,
  onEditor,
  locks = NO_LOCKS,
  sectionOffset = 0,
  connectionId,
  onCopyRef,
  onLockRejected,
  readOnly = false,
  releaseSignal = 0,
  room = null,
  wsUrl,
  onNotice,
}: RichEditorProps) {
  const { t } = useLang()
  // Improve-with-AI is part of the local-AI surface — hidden when the capability gate is OFF.
  const agentAvailable = useAgentAvailable()
  // Stable per-mount origin tag so our own writes are ignored by the observer.
  const origin = useMemo(() => ({ src: 'rich-editor' }), [])

  // Stable handle for the paste/drop handlers (editorProps is built before `editor` exists).
  const editorRef = useRef<Editor | null>(null)
  // SectionNumbers reads its options ONCE, when the editor is created (tiptap v3 regenerates
  // the options object on every access, so it cannot be written to afterwards). Hand it stable
  // wrappers that read the latest props from here; the lock SET is published separately.
  const host = useRef({ t, onCopyRef, onLockRejected, room, wsUrl, onNotice, ydoc })
  useEffect(() => {
    host.current = { t, onCopyRef, onLockRejected, room, wsUrl, onNotice, ydoc }
  })

  // Who this editor writes as, for the lock carve-out. Per CONNECTION, never per identity: the
  // member's own console agent holds locks under the same userId and must still block them.
  const writer = connectionId ?? actor
  // The § the Y.Text flush may not touch, read through a ref because `onUpdate` is installed once
  // at editor creation and would otherwise close over the first render's locks forever.
  const lockedSections = useMemo(
    () => locks.filter((l) => l.actor !== writer).map((l) => l.section),
    [locks, writer],
  )
  const lockedRef = useRef(lockedSections)
  useEffect(() => {
    lockedRef.current = lockedSections
  }, [lockedSections])
  // Read through a ref for the same reason: `onUpdate` is installed once at editor creation and
  // would otherwise number the flush window under the offset of the first render forever.
  const offsetRef = useRef(sectionOffset)
  useEffect(() => {
    offsetRef.current = sectionOffset
  }, [sectionOffset])

  const imageFilesFrom = (list: FileList | null | undefined): File[] =>
    Array.from(list ?? []).filter((f) => f.type.startsWith('image/'))

  const editor = useEditor({
    extensions: [
      // StarterKit ships the Link mark; `isAllowedUri` narrows what an href may point at, whichever
      // way the link gets in (toolbar, paste, autolink, markdown from the CRDT, an AI edit).
      StarterKit.configure({ codeBlock: false, link: { isAllowedUri: isAllowedLinkUri } }),
      CodeBlockMermaid,
      // The table node is ours: column widths persist as % in the delimiter row, and a border drag
      // commits them once on mouseup (table-resize.ts). Rows and cells are the stock kit.
      ResizableTable.configure({ resizable: true }),
      TableKit.configure({ table: false }),
      ImageBlock.configure({ inline: false, allowBase64: false }),
      // The highlight ("surlignage"): `==words==` / `==words=={green}` / `==words=={#FFCC00}` in the markdown (highlight-mark.ts).
      ProseHighlight,
      // The text colour: `[words]{color=red}` / `[words]{color=#C00000}` in the markdown (text-colour-mark.ts).
      ProseTextColour,
      // The anchor (AB#350): `[words]{anchor=<id> name="…"}` in the markdown (anchor-mark.ts).
      ProseAnchor,
      Markdown.configure({ html: false, transformPastedText: true }),
      // § outline numbers on every heading + the shading/read-only enforcement of held §.
      SectionNumbers.configure({
        locks,
        offset: sectionOffset,
        locksVersion: 0,
        // The holder carve-out compares this with the lock's `actor`; see the Canvas mapping.
        currentActor: writer,
        copyLabel: (number, title) =>
          `${host.current.t('sec_copy_ref')} ${number}${title ? ` — ${title}` : ''}`,
        onCopyRef: (number, title) => host.current.onCopyRef?.(number, title),
        onRejected: () => host.current.onLockRejected?.(),
      }),
      // Find/Replace's own decorations — inert until components/FindReplace.tsx publishes a
      // match set through setFindHighlight(). Always present, like SectionNumbers above, rather
      // than conditionally mounted, so opening the panel never remounts (and reparses) the editor.
      FindHighlight,
      // Tab / Shift+Tab on a heading move it one level down / up (AB#319); elsewhere Tab is unchanged.
      HeadingLevelKeys,
    ],
    content: ytext.toString(),
    editable: !readOnly,
    editorProps: {
      attributes: { 'aria-label': 'Rich document editor', class: 'rich' },
      // Paste or drop an image → upload to the blob store, insert `![](url)`.
      handlePaste: (_view, event) => {
        const files = imageFilesFrom((event as ClipboardEvent).clipboardData?.files)
        if (files.length === 0) return false
        event.preventDefault()
        const ed = editorRef.current
        if (ed) files.forEach((f) => void uploadAndInsertImage(ed, f))
        return true
      },
      // A link to an anchor (AB#350, `#anchor-<id>`) is never opened in a new window (the Link
      // mark's own click handler would): a plain click places the caret, Ctrl/Cmd+click follows it.
      // Read-only too — following a link is reading.
      handleClick: (view, pos, event) => {
        const a = (event.target as Element | null)?.closest?.('a')
        const id = a && view.dom.contains(a) ? anchorIdOfHref(a.getAttribute('href') ?? '') : null
        if (!id) return false
        if (event.ctrlKey || event.metaKey) followAnchorLink(id)
        else view.dispatch(view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(pos))))
        return true
      },
      handleDOMEvents: {
        // …and never followed by the browser either (a read-only editor's links are live anchors).
        click: (view, event) => {
          const a = (event.target as Element | null)?.closest?.('a')
          if (a && view.dom.contains(a) && anchorIdOfHref(a.getAttribute('href') ?? '')) event.preventDefault()
          return false
        },
      },
      handleDrop: (_view, event) => {
        const files = imageFilesFrom((event as DragEvent).dataTransfer?.files)
        if (files.length === 0) return false
        event.preventDefault()
        const ed = editorRef.current
        if (ed) files.forEach((f) => void uploadAndInsertImage(ed, f))
        return true
      },
    },
    onUpdate: ({ editor }) => {
      // syncYText's lock-intersection check is the same soft, UX-only guard as SectionNumbers'
      // (the server is the real enforcement) — an unknown offset here just means "don't risk
      // resolving the wrong range", so 0 is an acceptable stand-in for this one check.
      if (
        syncYText(ytext, getMarkdown(editor), origin, lockedRef.current, offsetRef.current ?? 0) !==
        'blocked'
      ) {
        return
      }
      // The flush window reached into a § we do not hold, so nothing was written. The editor and
      // the Y.Text have diverged by exactly this edit; re-sync from the CRDT to drop it. Deferred
      // because we are inside ProseMirror's own dispatch — dispatching again from here re-enters
      // view.updateState. `emitUpdate: false` keeps setContent from re-triggering this handler.
      queueMicrotask(() => {
        if (editor.isDestroyed) return
        editor.commands.setContent(ytext.toString(), { emitUpdate: false })
        host.current.onLockRejected?.()
      })
    },
  })

  /** Select the anchor `id` in this part and scroll it into view; false when it is not here. */
  const revealHere = useCallback((id: string): boolean => {
    const ed = editorRef.current
    return !!ed && !ed.isDestroyed && revealAnchor(ed, id)
  }, [])
  /** Follow a link to the anchor `id` (Ctrl/Cmd+click): here, in another part, or a broken link. */
  function followAnchorLink(id: string): void {
    // Read through the ref: the click handler is installed once, at editor creation.
    const { room: r, wsUrl: ws, onNotice: notice, t: tr, ydoc: doc } = host.current
    if (!r || ws === undefined) return
    void followAnchor(id, { room: r, ydoc: doc }, ws, revealHere)
      .then((outcome) => {
        if (outcome === 'broken') notice?.(tr('an_broken'))
      })
      .catch(() => notice?.(tr('an_broken')))
  }
  // Arriving from another part's anchor link: select it once this part's content holds it.
  usePendingAnchor(room ?? '', revealHere, !!editor && !!room)

  // Keep the ref in sync so the paste/drop handlers can reach the live editor.
  useEffect(() => {
    editorRef.current = editor
  }, [editor])

  // Expose the editor to an external toolbar; clear it on teardown — AND at the moment tiptap
  // destroys it. useEditor destroys an instance on its own timer, well before this effect's
  // cleanup runs, and any host re-render in between would hand the dead instance to the toolbar
  // (whose guards are the safety net; this shortens the window).
  useEffect(() => {
    if (!editor) {
      onEditor?.(null)
      return
    }
    onEditor?.(editor)
    const onDestroy = (): void => onEditor?.(null)
    editor.on('destroy', onDestroy)
    return () => {
      editor.off('destroy', onDestroy)
      onEditor?.(null)
    }
  }, [editor, onEditor])

  // Pull remote changes (other clients / AI) into the rich view.
  // Also immediately syncs content when ytext changes (room switch): React 18 batches
  // setConn(null)+setConn(newConn) into one render so this component may not unmount
  // between rooms — without the initial sync the old room's content would persist until
  // the first remote transaction arrives.
  useEffect(() => {
    if (!editor) return
    const md = ytext.toString()
    if (md !== getMarkdown(editor)) editor.commands.setContent(md, { emitUpdate: false })
    const observer = (event: Y.YTextEvent): void => {
      if (event.transaction.origin === origin) return
      const md = ytext.toString()
      if (md !== getMarkdown(editor)) editor.commands.setContent(md, { emitUpdate: false })
    }
    ytext.observe(observer)
    return () => ytext.unobserve(observer)
  }, [editor, ytext, origin])

  // Publish the live lock set. setSectionLocks() is the ONLY supported update path — it
  // dispatches a meta-only transaction (no doc change), which the § filter always lets
  // through and which tiptap does not report as an update, so nothing reaches the Y.Text.
  useEffect(() => {
    if (!editor) return
    setSectionLocks(editor, locks)
  }, [editor, locks])

  // Same channel for the § offset — it moves whenever a sibling part of the deliverable gains
  // or loses a top-level §, which is a fact this client learns over HTTP, not through the CRDT.
  useEffect(() => {
    if (!editor) return
    setSectionOffset(editor, sectionOffset)
  }, [editor, sectionOffset])

  // Broadcast this user's caret/selection so peers can render a named cursor.
  useEffect(() => {
    if (!editor || !awareness) return
    const broadcast = (): void => {
      const { anchor, head } = editor.state.selection
      awareness.setLocalStateField('cursor', { anchor, head })
    }
    broadcast()
    editor.on('selectionUpdate', broadcast)
    return () => {
      editor.off('selectionUpdate', broadcast)
      awareness.setLocalStateField('cursor', null)
    }
  }, [editor, awareness])

  // Publish the § this member is working on — the human lock other members' agents must respect
  // (lock conflicts, AB#298). Separate from `cursor` (raw PM positions the server cannot map to a
  // §): `editSection` is the outline number + title of the deepest § holding the caret, numbered
  // like the claims. Null for a viewer, while idle, before the first heading, or until the offset
  // is known. A hidden tab keeps it (AB#292).
  const wrapRef = useRef<HTMLDivElement>(null)
  const idle = useIdle(wrapRef, !!awareness && !readOnly)
  const released = useReleased(wrapRef, releaseSignal)
  useEffect(() => {
    if (released && editor && !editor.isDestroyed) editor.commands.blur()
  }, [released, editor])
  useEffect(() => {
    if (!editor || !awareness) return
    let last: string | undefined
    const publish = (): void => {
      const value: EditSection | null =
        readOnly || idle || released || editor.isDestroyed
          ? null
          : editSectionAt(editor.state.doc, editor.state.selection.head, sectionOffset)
      // Every setLocalStateField is a network broadcast; a keystroke inside the same § is not news.
      const key = JSON.stringify(value)
      if (key === last) return
      last = key
      awareness.setLocalStateField('editSection', value)
    }
    publish()
    // 'transaction' covers caret moves AND heading edits (local or remote) that renumber the §.
    editor.on('transaction', publish)
    return () => {
      editor.off('transaction', publish)
      awareness.setLocalStateField('editSection', null)
    }
  }, [editor, awareness, readOnly, idle, released, sectionOffset])

  // Improve-with-AI (shared with the slides text, useImproveSelection.tsx): the selection is
  // replaced in place and recorded as an 'ai' history entry whose prompt is the instruction.
  const improve = useImproveSelection({
    editor,
    apply: (pending, improved, instruction) => {
      if (!editor) return
      const before = ytext.toString()
      suppressRef.current = true
      try {
        editor
          .chain()
          .focus()
          .command(({ tr, dispatch }) => {
            if (dispatch) dispatch(tr.insertText(improved, pending.from, pending.to))
            return true
          })
          .run()
      } finally {
        suppressRef.current = false
      }
      const after = ytext.toString()
      const entry = makeHistoryEntry({
        actor,
        kind: 'ai',
        before,
        after,
        ...(instruction ? { prompt: instruction } : {}),
      })
      if (entry) appendHistory(ydoc, entry)
    },
  })

  return (
    <>
      {editor && agentAvailable && !readOnly && (
        <BubbleMenu editor={editor} className="sel-bubble">
          <button className="sel-bubble-btn" onClick={() => void improve.open()} disabled={improve.agentBusy}>
            {t('improve_ai')}
          </button>
        </BubbleMenu>
      )}

      <div className="editor-wrap" ref={wrapRef}>
        <EditorContent editor={editor} />
        {editor && awareness && <CursorOverlay editor={editor} awareness={awareness} />}
      </div>

      {improve.popover}
    </>
  )
}
